import moment from "moment/moment";
import React from "react";
import { Link } from "react-router-dom";
import {
  FiAlertCircle,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiDownload,
  FiFileText,
  FiMessageCircle,
  FiPrinter,
  FiSend,
} from "react-icons/fi";
import Layout from "../../../components/layouts/Layout";
import { Table, TableBody, TableHeader } from "../../../components/table";
import {
  exportConversionsMonthlyOverviewCsv,
  fetchConversionsMonthlyOverview,
  fetchSmsBillingRecords,
} from "../AutomationTemplates/automationTemplateApi";
import SmsAccessGuard from "../Components/SmsAccessGuard";
import "../style/sms-billing.css";

const EMPTY_PAGINATION = {
  current_page: 1,
  last_page: 1,
  per_page: 5,
  total: 0,
  from: 0,
  to: 0,
};

const STATUS_LABELS = {
  unbilled: "Unbilled",
  invoiced: "Invoiced",
  paid: "Paid",
  void: "Void",
};

const getErrorMessage = (error, fallback) => {
  const message = error && error.response && error.response.data && error.response.data.message;
  const integrationMessage = error && error.integrationMessage;

  if (typeof message === "string" && message.trim()) return message;
  if (typeof integrationMessage === "string" && integrationMessage.trim()) return integrationMessage;
  return fallback;
};

const invalidResponseError = (payload) => {
  const error = new Error("Invalid conversations billing response");
  error.integrationMessage = payload && payload.message;
  return error;
};

const formatMoney = (value, currency) => {
  const amount = Number(value || 0);
  const currencyCode = currency || "AUD";

  try {
    return new Intl.NumberFormat("en-AU", { style: "currency", currency: currencyCode }).format(amount);
  } catch (error) {
    return `${currencyCode} ${amount.toFixed(2)}`;
  }
};

const formatNumber = (value) => Number(value || 0).toLocaleString();

const formatDate = (value) => {
  const date = moment(value);
  return value && date.isValid() ? date.format("MMM DD, YYYY") : "—";
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

class AnalyticsBilling extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedMonth: "",
      overview: null,
      records: [],
      pagination: { ...EMPTY_PAGINATION },
      loadingOverview: false,
      loadingRecords: false,
      exportingCsv: false,
      overviewError: "",
      recordsError: "",
    };
    this.isMountedFlag = false;
    this.overviewRequestId = 0;
    this.recordsRequestId = 0;
  }

  componentDidMount() {
    this.isMountedFlag = true;
    this.loadOverview();
  }

  componentWillUnmount() {
    this.isMountedFlag = false;
  }

  loadOverview = async (month) => {
    const requestId = ++this.overviewRequestId;
    this.setState({ loadingOverview: true, overviewError: "" });

    try {
      const response = await fetchConversionsMonthlyOverview(month ? { month } : undefined);
      const payload = response && response.data;
      const overview = payload && payload.data;

      if (!overview || !overview.billing_month || payload.success === false) {
        throw invalidResponseError(payload);
      }

      if (this.isMountedFlag && requestId === this.overviewRequestId) {
        this.setState({
          selectedMonth: overview.billing_month.value || month || "",
          overview,
          loadingOverview: false,
        });
        this.loadRecords(1, overview.billing_month);
      }
    } catch (error) {
      if (this.isMountedFlag && requestId === this.overviewRequestId) {
        this.setState((state) => ({
          selectedMonth: state.overview && state.overview.billing_month
            ? state.overview.billing_month.value
            : state.selectedMonth,
          loadingOverview: false,
          overviewError: getErrorMessage(error, "Monthly billing data is unavailable right now."),
        }));
      }
    }
  };

  loadRecords = async (page, billingMonth) => {
    const requestId = ++this.recordsRequestId;
    const month = billingMonth || (this.state.overview && this.state.overview.billing_month) || {};
    const params = { page: page || 1, per_page: 5 };
    if (month.date_from) params.date_from = month.date_from;
    if (month.date_to) params.date_to = month.date_to;

    this.setState({ loadingRecords: true, recordsError: "" });
    try {
      const response = await fetchSmsBillingRecords(params);
      const payload = response && response.data;
      const data = payload && payload.data;
      if (!data || !Array.isArray(data.billing_records) || payload.success === false) throw invalidResponseError(payload);

      if (this.isMountedFlag && requestId === this.recordsRequestId) {
        this.setState({
          records: data.billing_records,
          pagination: { ...EMPTY_PAGINATION, ...(data.pagination || {}) },
          loadingRecords: false,
        });
      }
    } catch (error) {
      if (this.isMountedFlag && requestId === this.recordsRequestId) {
        this.setState({
          records: [],
          pagination: { ...this.state.pagination, current_page: params.page },
          loadingRecords: false,
          recordsError: getErrorMessage(error, "Invoice and charge history is unavailable right now."),
        });
      }
    }
  };

  handleMonthChange = (event) => {
    const selectedMonth = event.target.value;
    this.setState({ selectedMonth }, () => this.loadOverview(selectedMonth));
  };

  handleExportCsv = async () => {
    const { overview, exportingCsv } = this.state;
    const month = overview && overview.billing_month && overview.billing_month.value;
    if (!month || exportingCsv) return;

    this.setState({ exportingCsv: true, overviewError: "" });
    try {
      const response = await exportConversionsMonthlyOverviewCsv({ month });
      downloadBlob(response.data, `conversations-billing-${month}.csv`);
    } catch (error) {
      if (this.isMountedFlag) this.setState({ overviewError: "The billing CSV could not be downloaded. Please try again." });
    } finally {
      if (this.isMountedFlag) this.setState({ exportingCsv: false });
    }
  };

  handleExportPdf = () => {
    if (this.state.overview) window.print();
  };

  changePage = (page) => {
    const { current_page, last_page } = this.state.pagination;
    if (page < 1 || page > last_page || page === current_page || this.state.loadingRecords) return;
    this.loadRecords(page);
  };

  renderRecords() {
    const { loadingRecords, records, recordsError, pagination, overview } = this.state;
    const currency = (overview && overview.currency) || (records[0] && records[0].currency) || "AUD";

    if (loadingRecords) {
      return <div className="billing-history-state"><span className="spinner-border" role="status" aria-label="Loading billing records" /></div>;
    }

    if (recordsError) {
      return <div className="billing-history-state error" role="alert"><span>{recordsError}</span><button type="button" onClick={() => this.loadRecords(pagination.current_page)}>Retry</button></div>;
    }

    return (
      <>
        <div className="billing-history-table-wrap">
          <Table>
            <TableHeader><tr><th>Date</th><th>Description</th><th>Replies</th><th>Amount</th><th>Status</th></tr></TableHeader>
            <TableBody>
              {records.length ? records.map((record) => {
                const status = String(record.billing_status || "").toLowerCase();
                const phone = record.phone_number || record.phone;
                return (
                  <tr key={record.id}>
                    <td>{formatDate(record.used_at || record.created_at)}</td>
                    <td>{phone ? `Customer reply from ${phone}` : "Customer SMS reply"}</td>
                    <td>{formatNumber(record.sms_count || record.sms_segments || 1)}</td>
                    <td>{formatMoney(record.total_price, record.currency || currency)}</td>
                    <td><span className={`sms-billing-status-badge status-${status || "unknown"}`}>{STATUS_LABELS[status] || status || "—"}</span></td>
                  </tr>
                );
              }) : <tr><td colSpan={5}><div className="billing-history-state">No billing records found for this month.</div></td></tr>}
            </TableBody>
          </Table>
        </div>
        {pagination.last_page > 1 ? (
          <div className="billing-history-pagination">
            <span>Showing {pagination.from || 0} to {pagination.to || records.length} of {pagination.total}</span>
            <div><button type="button" disabled={pagination.current_page <= 1} onClick={() => this.changePage(pagination.current_page - 1)}>‹</button><strong>{pagination.current_page}</strong><button type="button" disabled={pagination.current_page >= pagination.last_page} onClick={() => this.changePage(pagination.current_page + 1)}>›</button></div>
          </div>
        ) : null}
      </>
    );
  }

  render() {
    const { selectedMonth, overview, loadingOverview, exportingCsv, overviewError } = this.state;
    const data = overview || {};
    const billingMonth = data.billing_month || {};
    const overviewTotals = data.overview || {};
    const usage = data.usage_breakdown || {};
    const statusBreakdown = data.billing_status_breakdown || {};
    const currency = data.currency || "AUD";
    const availableMonths = data.available_months || (billingMonth.value ? [billingMonth] : []);
    const smsSent = Number(overviewTotals.sms_sent || 0);
    const failedSms = Number(usage.failed_sms || 0);
    const mainCards = [
      { label: "Billing Month", value: billingMonth.label || "—", icon: <FiCalendar />, tone: "gold" },
      { label: "Total Charged", value: formatMoney(overviewTotals.total_charged, currency), icon: <FiDollarSign />, tone: "green" },
      { label: "Current Outstanding Balance", value: formatMoney(overviewTotals.current_outstanding_balance, currency), icon: <FiAlertCircle />, tone: "gold" },
      { label: "Replies Billed", value: formatNumber(overviewTotals.replies_received), icon: <FiMessageCircle />, tone: "blue" },
      { label: "Invoiced", value: formatNumber(statusBreakdown.invoiced && statusBreakdown.invoiced.reply_count), icon: <FiFileText />, tone: "blue" },
      { label: "Paid", value: formatNumber(statusBreakdown.paid && statusBreakdown.paid.reply_count), icon: <FiCheckCircle />, tone: "green" },
    ];
    const usageCards = [
      { label: "Automation SMS", value: Number(usage.automation_sms || 0), detail: smsSent ? `${((Number(usage.automation_sms || 0) / smsSent) * 100).toFixed(1)}% of total` : "0% of total", icon: <FiMessageCircle />, tone: "gold" },
      { label: "Manual Outbound SMS", value: Number(usage.manual_outbound_sms || 0), detail: smsSent ? `${((Number(usage.manual_outbound_sms || 0) / smsSent) * 100).toFixed(1)}% of total` : "0% of total", icon: <FiSend />, tone: "blue" },
      { label: "Scheduled SMS", value: Number(usage.scheduled_sms || 0), detail: smsSent ? `${((Number(usage.scheduled_sms || 0) / smsSent) * 100).toFixed(1)}% of total` : "0% of total", icon: <FiClock />, tone: "green" },
      { label: "Failed SMS", value: failedSms, detail: `${((failedSms / Math.max(smsSent + failedSms, 1)) * 100).toFixed(1)}% failure rate`, icon: <FiAlertCircle />, tone: "red" },
    ];
    const statuses = ["unbilled", "invoiced", "paid", "void"];
    const tabs = [
      ["Overview", "/conversions"], ["Conversations", "/conversions/conversations"], ["Notifications", "/conversions/notifications"], ["Analytics", "/conversions/analytics"], ["My Automation Flows", "/conversions/automation-flows"], ["Browse Templates", "/conversions/sms-automation-templates"], ["Billing", "/conversions/billing", true],
    ];

    return (
      <SmsAccessGuard>
        <Layout>
          <main className="page billing-dashboard-page">
            <header className="billing-dashboard-header conversion-no-print">
              <div><h1>Billing</h1><p>Track your SMS usage, charges, and payment status in one place.</p></div>
              <div className="billing-dashboard-controls">
                <select aria-label="Billing month" value={selectedMonth} disabled={!overview || loadingOverview} onChange={this.handleMonthChange}>{availableMonths.map((month) => <option key={month.value} value={month.value}>{month.label}</option>)}</select>
                <button type="button" className="pdf" disabled={!overview || loadingOverview} onClick={this.handleExportPdf}><FiPrinter /> Export PDF</button>
                <button type="button" disabled={!overview || loadingOverview || exportingCsv} onClick={this.handleExportCsv}><FiDownload /> {exportingCsv ? "Exporting..." : "Export CSV"}</button>
              </div>
            </header>

            <nav className="conversations-tabs billing-dashboard-tabs conversion-no-print" aria-label="Conversations sections">{tabs.map(([label, path, active]) => <Link key={path} to={path} className={active ? "active" : ""}>{label}</Link>)}</nav>

            {overviewError ? <div className="billing-dashboard-alert" role="alert"><span>{overviewError}</span><button type="button" onClick={() => this.loadOverview(selectedMonth)}>Retry</button></div> : null}
            {!overview && loadingOverview ? <div className="billing-dashboard-initial"><span className="spinner-border" role="status" aria-label="Loading billing dashboard" /><p>Loading billing dashboard…</p></div> : null}

            {overview ? (
              <div className="billing-dashboard-content">
                {loadingOverview ? <div className="conversion-loading-overlay"><span className="spinner-border" role="status" aria-label="Loading selected billing month" /></div> : null}
                <h2 className="conversion-print-heading">SMS Billing — {billingMonth.label}</h2>
                <section className="billing-primary-grid">{mainCards.map((card) => <article className="billing-primary-card" key={card.label}><span className={`billing-card-icon ${card.tone}`}>{card.icon}</span><div><span>{card.label}</span><strong>{card.value}</strong></div></article>)}</section>

                <section className="billing-dashboard-panel"><header><h2>SMS Usage Breakdown</h2></header><div className="billing-usage-grid">{usageCards.map((card) => <article key={card.label}><span className={`billing-card-icon ${card.tone}`}>{card.icon}</span><div><span>{card.label}</span><strong>{formatNumber(card.value)}</strong><small>{card.detail}</small></div></article>)}</div></section>

                <section className="billing-dashboard-panel"><header><h2>Billing Status Breakdown</h2></header><div className="billing-status-grid">{statuses.map((status) => { const item = statusBreakdown[status] || {}; return <article key={status}><span className={`sms-billing-status-badge status-${status}`}>{STATUS_LABELS[status]}</span><strong>{formatMoney(item.amount, currency)}</strong><small>{formatNumber(item.reply_count)} replies</small></article>; })}</div></section>

                <section className="billing-dashboard-panel billing-history-panel"><header><h2>Invoice / Charge History</h2></header>{this.renderRecords()}</section>
                <p className="billing-postpaid-note">Postpaid billing · {formatMoney(data.price_per_reply, currency)} per successful customer reply</p>
              </div>
            ) : null}
          </main>
        </Layout>
      </SmsAccessGuard>
    );
  }
}

export default AnalyticsBilling;
